(() => {
  if (!window.customElements || !window.HTMLDialogElement || !HTMLDialogElement.prototype.showModal || customElements.get('bc-mobile-filters')) return;

  const normalizePrice = value => {
    const text = value.trim().replace(/[\s\u00a0\u202f]/g, '').replace(',', '.');
    if (!text) return '';
    return /^(?:\d+(?:\.\d{0,2})?|\.\d{1,2})$/.test(text) && Number.isFinite(Number(text)) ? String(Number(text)) : null;
  };

  class BookCafeMobileFilters extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.dialog = this.querySelector('dialog');
      this.form = this.querySelector('form');
      this.opener = this.querySelector('[data-bc-mf-open]');
      if (!this.dialog || !this.form || !this.opener) return;
      this.ready = true;
      this.controls = [...this.form.querySelectorAll('input[name^="filter."]')];
      this.media = matchMedia('(max-width: 719px)');
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.opener.addEventListener('click', () => this.open(), options);
      this.querySelector('[data-bc-mf-close]').addEventListener('click', () => this.close(), options);
      this.querySelector('[data-bc-mf-clear]').addEventListener('click', () => this.clear(), options);
      this.dialog.addEventListener('cancel', event => { event.preventDefault(); this.close(); }, options);
      this.dialog.addEventListener('close', () => this.afterClose(), options);
      this.form.addEventListener('submit', event => this.apply(event), options);
      this.form.addEventListener('change', event => event.stopPropagation(), options);
      this.form.addEventListener('input', event => {
        event.stopPropagation();
        if (event.target.matches('[data-bc-mf-search]')) this.searchValues(event.target);
        if (event.target.matches('[data-bc-mf-price]')) this.resetErrors();
      }, options);
      this.media.addEventListener('change', () => { if (!this.media.matches) this.close(); }, options);
      this.section = this.closest('.shopify-section') || this.parentElement;
      this.section.setAttribute('data-bc-mobile-filters-ready', '');
      this.hidden = false;
    }

    disconnectedCallback() {
      if (this.dialog && this.dialog.open) this.dialog.close();
      document.documentElement.classList.remove('bc-mf-is-open');
      this.abort?.abort();
      this.section?.removeAttribute('data-bc-mobile-filters-ready');
      this.ready = false;
    }

    resetErrors() {
      this.querySelectorAll('[data-bc-mf-error]').forEach(error => { error.hidden = true; error.textContent = ''; });
      this.controls.forEach(input => input.removeAttribute('aria-invalid'));
    }

    resetSearch() {
      this.querySelectorAll('[data-bc-mf-search]').forEach(input => { input.value = ''; this.searchValues(input); });
    }

    restore() {
      const params = new URL(location.href).searchParams;
      this.controls.forEach(input => {
        const values = params.getAll(input.name);
        if (input.type === 'checkbox') {
          input.checked = values.some(value => value === input.value || value.split(',').includes(input.value));
          if (input.checked) input.disabled = false;
        } else input.value = values[0] || '';
      });
      this.resetErrors();
      this.resetSearch();
    }

    open() {
      if (!this.media.matches || this.dialog.open) return;
      this.restore();
      this.dialog.showModal();
      document.documentElement.classList.add('bc-mf-is-open');
      this.querySelector('[data-bc-mf-close]').focus({ preventScroll: true });
    }

    close() {
      if (this.dialog.open) this.dialog.close();
    }

    afterClose() {
      document.documentElement.classList.remove('bc-mf-is-open');
      this.restore();
      if (this.isConnected && this.media.matches) this.opener.focus({ preventScroll: true });
    }

    clear() {
      this.controls.forEach(input => { if (input.type === 'checkbox') input.checked = false; else input.value = ''; });
      this.resetErrors();
      this.resetSearch();
    }

    searchValues(input) {
      const group = input.closest('.bc-mf__group');
      const query = input.value.trim().toLocaleLowerCase();
      let visible = 0;
      group.querySelectorAll('[data-bc-mf-value]').forEach(item => {
        item.hidden = !item.querySelector('input').checked && !item.dataset.label.toLocaleLowerCase().includes(query);
        if (!item.hidden) visible += 1;
      });
      group.querySelector('[data-bc-mf-no-values]').hidden = visible > 0;
    }

    apply(event) {
      event.preventDefault();
      event.stopPropagation();
      this.resetErrors();
      let invalid = null;
      const normalized = new Map();
      this.querySelectorAll('[data-bc-mf-price-group]').forEach(group => {
        const min = group.querySelector('[data-bc-mf-price="min"]');
        const max = group.querySelector('[data-bc-mf-price="max"]');
        const low = normalizePrice(min.value);
        const high = normalizePrice(max.value);
        const error = group.querySelector('[data-bc-mf-error]');
        let input;
        if (low === null || high === null) {
          input = low === null ? min : max;
          error.textContent = error.dataset.invalidPrice;
        } else if (low !== '' && high !== '' && Number(low) > Number(high)) {
          input = min;
          error.textContent = error.dataset.invalidRange;
        }
        if (input) {
          input.setAttribute('aria-invalid', 'true');
          group.closest('details').open = true;
          error.hidden = false;
          invalid ||= input;
        } else {
          normalized.set(min.name, low);
          normalized.set(max.name, high);
        }
      });
      if (invalid) { invalid.focus(); return; }
      const url = new URL(location.href);
      url.pathname = new URL(this.form.action, location.href).pathname;
      url.searchParams.delete('page');
      url.searchParams.delete('section_id');
      url.searchParams.delete('sections');
      new Set(this.controls.map(input => input.name)).forEach(name => url.searchParams.delete(name));
      this.controls.forEach(input => {
        if (input.type === 'checkbox') {
          if (input.checked) url.searchParams.append(input.name, input.value);
        } else {
          const value = normalized.get(input.name);
          if (value !== undefined && value !== '') url.searchParams.append(input.name, value);
        }
      });
      this.form.querySelectorAll('input[type="hidden"]').forEach(input => {
        if (input.value && !url.searchParams.has(input.name)) url.searchParams.set(input.name, input.value);
      });
      url.hash = '';
      location.assign(url.href);
    }
  }

  customElements.define('bc-mobile-filters', BookCafeMobileFilters);
})();
